import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "react-toastify";
import React from "react";
import { errorMonitor } from "./errorMonitoring";
import { NavigateFunction } from "react-router";
import { routeToErrorPage, shouldShowErrorPage } from "./errorRouting";
import { isLocalDevelopment } from "./environment";
import { errorThrottling, ERROR_THROTTLE_CONFIGS } from "./errorThrottling";
import { executeFallbackFromError } from "./systemFallback";

// Estender o tipo do Axios para incluir metadata personalizada
declare module "axios" {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      requestId?: string;
      startTime?: number;
      retry?: RetryConfig;
    };
    _retry?: boolean;
    _retryCount?: number;
  }
}

// Tipos para configuração de retry
interface RetryConfig {
  retries: number;
  retryDelay: number;
  retryCondition?: (error: AxiosError) => boolean;
}

// Configuração global de interceptors
export const setupAxiosInterceptors = (navigate?: NavigateFunction) => {
  // Request Interceptor - Adicionar token automaticamente
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Adicionar token se disponível
      const token = getValidToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Adicionar ID de requisição para rastreamento
      config.metadata = {
        requestId: generateRequestId(),
        startTime: Date.now(),
      };

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response Interceptor - Tratamento global de erros
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      // Log de sucesso em desenvolvimento e métricas de performance
      if (response.config.metadata?.startTime) {
        const duration = Date.now() - response.config.metadata.startTime;
        const url = response.config.url || "";
        const method = response.config.method?.toUpperCase() || "GET";

        // Log no sistema de monitoramento
        errorMonitor.logApiCall(url, method, duration, response.status);
      }

      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig;

      // Log de erro e métricas
      if (originalRequest?.metadata?.startTime) {
        const duration = Date.now() - originalRequest.metadata.startTime;
        const url = originalRequest.url || "";
        const method = originalRequest.method?.toUpperCase() || "GET";
        const status = error.response?.status;

        // Log no sistema de monitoramento
        errorMonitor.logApiCall(url, method, duration, status, error);
      }

      // Tratar diferentes tipos de erro
      if (error.response) {
        // Erro do servidor (status 4xx, 5xx)
        await handleServerError(error, originalRequest, navigate);
      } else if (error.request) {
        // Erro de rede/conectividade
        await handleNetworkError(originalRequest, navigate);
      } else {
        // Erro de configuração ou outro
        handleGenericError(error, navigate);
      }

      // Se todas as tentativas de tratamento falharam, executar fallback
      const context = `api-${originalRequest?.method || "unknown"}-${originalRequest?.url || "unknown"}`;
      await executeFallbackFromError(error, context, navigate);

      return Promise.reject(error);
    }
  );
};

// Função para obter token válido (importar do auth.ts)
const getValidToken = (): string | null => {
  const persistentToken = localStorage.getItem("userToken");
  const persistentExpiry = localStorage.getItem("tokenExpiresAt");

  if (
    persistentToken &&
    persistentExpiry &&
    Date.now() < parseInt(persistentExpiry)
  ) {
    return persistentToken;
  }

  const sessionToken = sessionStorage.getItem("userToken");
  const sessionExpiry = sessionStorage.getItem("tokenExpiresAt");

  if (sessionToken && sessionExpiry && Date.now() < parseInt(sessionExpiry)) {
    return sessionToken;
  }

  return null;
};

// Gerar ID único para requisições
const generateRequestId = (): string => {
  return `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Tratar erros do servidor
const handleServerError = async (
  error: AxiosError,
  originalRequest: InternalAxiosRequestConfig,
  navigate?: NavigateFunction
) => {
  const status = error.response?.status;
  const data = error.response?.data as any;

  // Aplicar throttling para erros do servidor
  const errorKey = errorThrottling.generateErrorKey(error);
  const shouldLog = errorThrottling.shouldLogError(
    errorKey,
    ERROR_THROTTLE_CONFIGS.API_ERROR
  );

  if (!shouldLog) {
    return; // Erro está sendo throttled
  }

  errorThrottling.recordError(errorKey);

  // Verificar se devemos navegar para página de erro
  // Se sim, não mostrar toasts redundantes - a página de erro já informa o usuário
  if (navigate && shouldShowErrorPage(error)) {
    routeToErrorPage({
      navigate,
      error,
      context: "api_server_error",
    });
    return; // Early return - não executar handlers que mostram toasts
  }

  // Se não há navegação ou não é erro crítico, usar toasts para feedback inline
  switch (status) {
    case 401:
      handleUnauthorizedError();
      break;
    case 403:
      handleForbiddenError();
      break;
    case 404:
      handleNotFoundError(originalRequest);
      break;
    case 422:
      handleValidationError(data);
      break;
    case 429:
      await handleRateLimitError(error, originalRequest, navigate);
      break;
    case 500:
    case 502:
    case 503:
    case 504:
      await handleServerDownError(originalRequest, navigate);
      break;
    default:
      handleGenericServerError(error, navigate);
  }
};

// Tratar erros de rede
const handleNetworkError = async (
  originalRequest: InternalAxiosRequestConfig,
  navigate?: NavigateFunction
) => {
  // Aplicar throttling para erros de rede
  const networkError = { code: "ERR_NETWORK", config: originalRequest };
  const errorKey = errorThrottling.generateErrorKey(networkError);
  const shouldLog = errorThrottling.shouldLogError(
    errorKey,
    ERROR_THROTTLE_CONFIGS.NETWORK_ERROR
  );

  if (!shouldLog) {
    return; // Erro está sendo throttled
  }

  errorThrottling.recordError(errorKey);

  // Verificar se é um erro de conectividade
  if (!navigator.onLine) {
    // Em desenvolvimento local, não navegar para página offline
    if (isLocalDevelopment()) {
      const notificationKey = errorThrottling.generateNotificationKey(
        "info",
        "offline-dev"
      );
      if (errorThrottling.shouldShowNotification(notificationKey)) {
        errorThrottling.recordNotification(notificationKey);
        toast.info(
          "Modo offline (desenvolvimento) - as funcionalidades continuam disponíveis."
        );
      }
      return;
    }
    handleOfflineError(navigate);
    return;
  }

  // Se temos navegação disponível e é um erro crítico, navegar para página de erro
  if (navigate) {
    const networkError = new Error("Network Error");
    if (shouldShowErrorPage(networkError)) {
      routeToErrorPage({
        navigate,
        error: networkError,
        context: "api_network_error",
      });
      return;
    }
  }

  // Tentar retry para erros de rede
  if (shouldRetryRequest(originalRequest)) {
    try {
      await retryRequest(originalRequest);
    } catch (retryError) {
      // Apenas mostrar toast se não temos navegação ou não é erro crítico
      if (!navigate || !shouldShowErrorPage(retryError)) {
        showNetworkErrorNotification();
      }
    }
  } else {
    // Apenas mostrar toast se não temos navegação ou não é erro crítico
    if (!navigate || !shouldShowErrorPage(new Error("Network Error"))) {
      showNetworkErrorNotification();
    }
  }
};

// Manipuladores específicos de erro
const handleUnauthorizedError = () => {
  // Verificar se já estamos na página de login - se sim, não redirecionar
  const currentPath = window.location.pathname;
  const isLoginPage =
    currentPath === "/signin" ||
    currentPath === "/signup" ||
    currentPath === "/recover";

  // Limpar dados de autenticação
  localStorage.removeItem("userToken");
  localStorage.removeItem("tokenExpiresAt");
  sessionStorage.removeItem("userToken");
  sessionStorage.removeItem("tokenExpiresAt");

  // Só redirecionar se não estivermos em páginas de autenticação
  if (!isLoginPage) {
    setTimeout(() => {
      window.location.href = "/signin";
    }, 2000);
  }
  // Se estivermos na página de login, deixar o componente handle o erro com notificação
};

const handleForbiddenError = () => {
  window.location.href = "/forbidden";
};

const handleNotFoundError = (originalRequest: InternalAxiosRequestConfig) => {
  const isApiCall = originalRequest?.url?.includes("/api/");

  if (isApiCall) {
    toast.error("Recurso não encontrado.");
  }
  // Para páginas, deixar o router lidar com 404
};

const handleValidationError = (data: any) => {
  let message = "Dados inválidos. Verifique os campos e tente novamente.";

  if (data?.message) {
    message = data.message;
  } else if (data?.errors) {
    // Mostrar primeiro erro de validação
    const firstError = Object.values(data.errors)[0];
    if (Array.isArray(firstError)) {
      message = firstError[0] as string;
    }
  }

  // Aplicar throttling para mensagens de validação
  const notificationKey = errorThrottling.generateNotificationKey(
    "validation",
    message
  );
  if (
    errorThrottling.shouldShowNotification(
      notificationKey,
      ERROR_THROTTLE_CONFIGS.VALIDATION_ERROR
    )
  ) {
    errorThrottling.recordNotification(notificationKey);
    toast.error(message);
  }
};

const handleRateLimitError = async (
  error: AxiosError,
  originalRequest: InternalAxiosRequestConfig,
  navigate?: NavigateFunction
) => {
  const retryAfter = error.response?.headers["retry-after"];
  const delay = retryAfter ? parseInt(retryAfter) * 1000 : 60000; // 1 minuto padrão

  // Rate limiting é tratado com toast (não é erro crítico para página)
  showThrottledToast(
    "warning",
    `Muitas requisições. Tentando novamente em ${delay / 1000} segundos...`,
    "rate-limit-warning"
  );

  // Retry após o delay especificado
  await new Promise((resolve) => setTimeout(resolve, delay));

  try {
    return await axios.request(originalRequest);
  } catch (retryError) {
    // Se retry falha e é erro crítico, pode redirecionar
    if (navigate && shouldShowErrorPage(retryError)) {
      routeToErrorPage({
        navigate,
        error: retryError,
        context: "rate_limit_retry_failed",
      });
    } else {
      showThrottledToast(
        "error",
        "Serviço temporariamente indisponível. Tente novamente mais tarde.",
        "rate-limit-failed"
      );
    }
  }
};

const handleServerDownError = async (
  originalRequest: InternalAxiosRequestConfig,
  navigate?: NavigateFunction
) => {
  // Se temos navegação e é um erro crítico, navegar para página de erro
  if (navigate && shouldShowErrorPage({ response: { status: 500 } })) {
    routeToErrorPage({
      navigate,
      error: new Error("Server Down"),
      context: "server_down_error",
    });
    return;
  }

  // Implementar retry com backoff exponencial apenas se não redirecionamos
  if (shouldRetryRequest(originalRequest)) {
    const retryCount = (originalRequest._retryCount || 0) + 1;
    const delay = Math.min(1000 * Math.pow(2, retryCount), 30000); // Max 30 segundos

    // Apenas mostrar toast se não redirecionamos para página de erro
    toast.warning(
      `Erro do servidor. Tentando novamente em ${delay / 1000} segundos...`
    );

    await new Promise((resolve) => setTimeout(resolve, delay));

    originalRequest._retryCount = retryCount;

    try {
      return await axios.request(originalRequest);
    } catch (retryError) {
      if (retryCount >= 3) {
        // Apenas mostrar toast se não temos navegação disponível
        if (!navigate) {
          toast.error("Servidor indisponível. Tente novamente mais tarde.");
        }
        // Se temos navegação, já foi redirecionado acima
      }
    }
  }
};

const handleGenericServerError = (
  error: AxiosError,
  navigate?: NavigateFunction
) => {
  // Se temos navegação e é um erro crítico, não mostrar toast
  if (navigate && shouldShowErrorPage(error)) {
    return; // Já foi redirecionado em handleServerError
  }

  const data = error.response?.data as any;
  const message = data?.message || "Erro interno do servidor.";
  toast.error(message);
};

const handleGenericError = (error: AxiosError, navigate?: NavigateFunction) => {
  if (navigate && shouldShowErrorPage(error)) {
    routeToErrorPage({
      navigate,
      error,
      context: "api_generic_error",
    });
  } else {
    // Apenas mostrar toast se não redirecionamos
    toast.error("Erro inesperado. Tente novamente.");
  }
};

const handleOfflineError = (navigate?: NavigateFunction) => {
  // Em desenvolvimento local, apenas mostrar toast informativo
  if (isLocalDevelopment()) {
    toast.info(
      "Modo offline (desenvolvimento) - as funcionalidades continuam disponíveis."
    );
    return;
  }

  if (navigate) {
    // Navegar para página offline - não mostrar toast
    routeToErrorPage({
      navigate,
      error: new Error("No internet connection"),
      context: "offline",
    });
  } else {
    // Fallback para toast se não temos navegação
    toast.error(
      "Sem conexão à internet. Algumas funcionalidades podem não estar disponíveis."
    );
  }
};

const showNetworkErrorNotification = () => {
  const message = "Erro de conectividade. Verifique sua conexão à internet.";
  const notificationKey = errorThrottling.generateNotificationKey(
    "error",
    "network-connectivity"
  );

  if (
    errorThrottling.shouldShowNotification(
      notificationKey,
      ERROR_THROTTLE_CONFIGS.NETWORK_ERROR
    )
  ) {
    errorThrottling.recordNotification(notificationKey);
    toast.error(message);
  }
};

// Helper para mostrar toasts com throttling
const showThrottledToast = (
  type: "success" | "error" | "warning" | "info",
  message: string,
  throttleKey?: string,
  throttleConfig?: any
) => {
  const key =
    throttleKey || errorThrottling.generateNotificationKey(type, message);

  if (errorThrottling.shouldShowNotification(key, throttleConfig)) {
    errorThrottling.recordNotification(key);
    toast[type](message);
    return true;
  }
  return false;
};

// Verificar se deve fazer retry
const shouldRetryRequest = (
  originalRequest: InternalAxiosRequestConfig
): boolean => {
  const retryCount = originalRequest._retryCount || 0;
  const maxRetries = 3;

  // Não fazer retry se já tentou muitas vezes
  if (retryCount >= maxRetries) {
    return false;
  }

  // Não fazer retry para requests que alteraram dados (POST, PUT, DELETE)
  const method = originalRequest.method?.toLowerCase();
  if (method && ["post", "put", "delete", "patch"].includes(method)) {
    return false;
  }

  return true;
};

// Fazer retry da requisição
const retryRequest = async (originalRequest: InternalAxiosRequestConfig) => {
  const retryCount = (originalRequest._retryCount || 0) + 1;
  originalRequest._retryCount = retryCount;

  // Delay progressivo
  const delay = Math.min(1000 * retryCount, 5000);
  await new Promise((resolve) => setTimeout(resolve, delay));

  return axios.request(originalRequest);
};

// Configurar retry personalizado para requisições específicas
export const axiosWithRetry = (
  config: AxiosRequestConfig
  // retryConfig?: Partial<RetryConfig>
) => {
  // const defaultRetryConfig: RetryConfig = {
  //   retries: 3,
  //   retryDelay: 1000,
  //   retryCondition: (error: AxiosError) => {
  //     return !error.response || error.response.status >= 500;
  //   },
  // };

  // const finalConfig = { ...defaultRetryConfig, ...retryConfig };

  return axios.request({
    ...config,
    // Adicionar configuração de retry personalizada
    timeout: config.timeout || 10000,
  });
};

// Exportar configurações úteis
export const createApiClient = (baseURL: string) => {
  const client = axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return client;
};

// Hook para monitoramento de status de rede
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("Conexão restabelecida!");
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (isLocalDevelopment()) {
        toast.info("Modo offline ativado (desenvolvimento)");
      } else {
        toast.warning(
          "Conexão perdida. Algumas funcionalidades podem não funcionar."
        );
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return { isOnline };
};
